import { Controller } from "@hotwired/stimulus";
import Trix from "trix";

export default class extends Controller {
  static targets = [];

  connect() {
    this.youtubePattern =
      /^https:\/\/(([^\.]+\.)?youtube\.com\/watch\?.*?v=|youtu\.be\/)([a-zA-Z0-9_-]{11}).*/;
    this.pdfPattern = /^https?:\/\/.*\.pdf(\?.*)?$/i;
    this.currentEmbed = null;

    // Set up custom toolbar
    Trix.config.toolbar.getDefaultHTML = this.toolbarDefaultHTML.bind(this);

    // Initialize when Trix editor is ready
    this.element.addEventListener(
      "trix-initialize",
      this.initializeEditor.bind(this),
    );
  }

  initializeEditor(event) {
    this.editor = event.target.editor;
    this.toolbar = event.target.toolbarElement;

    // Update toolbar HTML
    this.updateToolbar();

    // Set up event listeners for dynamically created elements
    this.setupEventListeners();

    // Listen for content changes to auto-embed YouTube URLs
    this.element.addEventListener(
      "trix-change",
      this.handleContentChange.bind(this),
    );

    this.reset();
  }

  updateToolbar() {
    if (this.toolbar) {
      this.toolbar.innerHTML = this.toolbarDefaultHTML();
    }
  }

  setupEventListeners() {
    // Find the href input and embed elements in the toolbar
    this.hrefInput = this.toolbar?.querySelector(
      "[data-trix-input][name='href']",
    );
    this.embedButton = this.toolbar?.querySelector(
      "[data-behavior='embed_url']",
    );

    // Add event listeners
    if (this.hrefInput) {
      this.hrefInput.addEventListener("input", this.handleUrlInput.bind(this));
      this.hrefInput.addEventListener(
        "focusin",
        this.handleUrlInput.bind(this),
      );
    }

    if (this.embedButton) {
      this.embedButton.addEventListener("click", this.embedMedia.bind(this));
    }
  }

  handleUrlInput(event) {
    const value = event.target.value.trim();
    const youtubeMatches = value.match(this.youtubePattern);
    const pdfMatches = value.match(this.pdfPattern);

    if (youtubeMatches != null) {
      this.fetchEmbedData(youtubeMatches[3], "dialog");
    } else if (pdfMatches != null) {
      this.fetchEmbedData(value, "dialog");
    } else {
      this.reset();
    }
  }

  handleContentChange(event) {
    // Small delay to ensure content is fully updated
    setTimeout(() => {
      this.scanAndReplaceEmbedUrls();
    }, 100);
  }

  scanAndReplaceEmbedUrls() {
    const document = this.editor.getDocument();
    const blocks = document.getBlocks();

    blocks.forEach((block, blockIndex) => {
      const text = block.toString();
      const youtubeMatches = text.match(this.youtubePattern);
      const pdfMatches = text.match(this.pdfPattern);

      if (youtubeMatches) {
        const videoId = youtubeMatches[3];
        const url = youtubeMatches[0];

        // Find the position of the URL in the block
        const urlStart = text.indexOf(url);
        if (urlStart !== -1) {
          this.replaceUrlWithEmbed(blockIndex, urlStart, url.length, videoId);
        }
      } else if (pdfMatches) {
        const url = pdfMatches[0];

        // Find the position of the URL in the block
        const urlStart = text.indexOf(url);
        if (urlStart !== -1) {
          this.replaceUrlWithEmbed(blockIndex, urlStart, url.length, url);
        }
      }
    });
  }

  async replaceUrlWithEmbed(blockIndex, urlStart, urlLength, embedId) {
    try {
      const response = await fetch(
        `/embeds?url=${encodeURIComponent(embedId)}`,
      );
      const embed = await response.text();

      // Calculate the position in the document
      const document = this.editor.getDocument();
      let position = 0;

      // Add up lengths of all blocks before the target block
      for (let i = 0; i < blockIndex; i++) {
        position += document.getBlockAtIndex(i).getLength();
      }

      // Add the position within the block
      position += urlStart;

      // Select and replace the URL text
      this.editor.setSelectedRange([position, position + urlLength]);

      // Insert the embed attachment
      const attachment = new Trix.Attachment(JSON.parse(embed));
      this.editor.insertAttachment(attachment);
    } catch (error) {
      console.error("Failed to fetch embed data for auto-embed:", error);
    }
  }

  async fetchEmbedData(embedId, source = "dialog") {
    const url = `/embeds?url=${encodeURIComponent(embedId)}`;

    try {
      const response = await fetch(url);
      const embed = await response.text();

      if (source === "dialog") {
        this.showEmbedOption(embed);
      }
    } catch (error) {
      console.error("Failed to fetch embed data:", error);
      if (source === "dialog") {
        this.reset();
      }
    }
  }

  embedMedia() {
    if (this.currentEmbed == null) {
      return;
    }

    const attachment = new Trix.Attachment(JSON.parse(this.currentEmbed));
    this.editor.insertAttachment(attachment);
    this.element.focus();
  }

  showEmbedOption(embed) {
    this.currentEmbed = embed;
    if (this.embedButton) {
      this.embedButton.style.display = "block";
    }
  }

  reset() {
    if (this.embedButton) {
      this.embedButton.style.display = "none";
    }
    this.currentEmbed = null;
  }

  toolbarDefaultHTML() {
    const { lang } = Trix.config;

    return `
      <div class='trix-button-row'>
        <span class='trix-button-group trix-button-group--text-tools' data-trix-button-group='text-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-bold' data-trix-attribute='bold' data-trix-key='b' title='${lang.bold}' tabindex='-1'>${lang.bold}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-italic' data-trix-attribute='italic' data-trix-key='i' title='${lang.italic}' tabindex='-1'>${lang.italic}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-strike' data-trix-attribute='strike' title='${lang.strike}' tabindex='-1'>${lang.strike}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-link' data-trix-attribute='href' data-trix-action='link' data-trix-key='k' title='${lang.link}' tabindex='-1'>${lang.link}</button>
        </span>
        <span class='trix-button-group trix-button-group--block-tools' data-trix-button-group='block-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-heading-1' data-trix-attribute='heading1' title='${lang.heading1}' tabindex='-1'>${lang.heading1}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-quote' data-trix-attribute='quote' title='${lang.quote}' tabindex='-1'>${lang.quote}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-bullet-list' data-trix-attribute='bullet' title='${lang.bullets}' tabindex='-1'>${lang.bullets}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-number-list' data-trix-attribute='number' title='${lang.numbers}' tabindex='-1'>${lang.numbers}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-decrease-nesting-level' data-trix-action='decreaseNestingLevel' title='${lang.outdent}' tabindex='-1'>${lang.outdent}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-increase-nesting-level' data-trix-action='increaseNestingLevel' title='${lang.indent}' tabindex='-1'>${lang.indent}</button>
        </span>
        <span class='trix-button-group trix-button-group--file-tools' data-trix-button-group='file-tools'>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-file" data-trix-action="attachFiles" title="${lang.attachFiles}" tabindex="-1"><i class="fas fa-image"></i></button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-attach' data-trix-action='attachFiles' title='${lang.attachFiles}' tabindex='-1'>${lang.attachFiles}</button>
        </span>
        <span class='trix-button-group-spacer'></span>
        <span class='trix-button-group trix-button-group--history-tools' data-trix-button-group='history-tools'>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-undo' data-trix-action='undo' data-trix-key='z' title='${lang.undo}' tabindex='-1'>${lang.undo}</button>
          <button type='button' class='trix-button trix-button--icon trix-button--icon-redo' data-trix-action='redo' data-trix-key='shift+z' title='${lang.redo}' tabindex='-1'>${lang.redo}</button>
        </span>
      </div>
      <div class='trix-dialogs' data-trix-dialogs>
        <div class='trix-dialog trix-dialog--link' data-trix-dialog='href' data-trix-dialog-attribute='href'>
          <div class='trix-dialog__link-fields'>
            <input type='url' name='href' class='trix-input trix-input--dialog' placeholder='${lang.urlPlaceholder}' aria-label='${lang.url}' data-trix-input>
            <div class='d-flex gap-1'>
              <input type='button' class='btn btn-secondary btn-small mr-1' data-behavior='embed_url' value='Embed Media'>
              <input type='button' class='btn btn-secondary btn-small mr-1' value='${lang.link}' data-trix-method='setAttribute'>
              <input type='button' class='btn btn-tertiary outline btn-small' value='${lang.unlink}' data-trix-method='removeAttribute'>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
